import { useEffect, useMemo, useState } from "react";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../../../models/chart_model";
import loader from "../../../../../../components/recharts/loader.gif";
import {
  getTopBuyersSellers,
  getTopBuyerSellerSlotwise,
  TopBuyerSellerResponse,
} from "../../../../../../Rest_api/RestApi";
import BuySellDelta from "./BuySellDelta";

export const DataTabs = ["Buy/Sell", "Buy/Sell Delta"];

export const VIEW_OPTIONS = [
  { id: "buyers", label: "Buyers" },
  { id: "sellers", label: "Sellers" },
];

export const DELTA_VIEW_OPTIONS = [
  { id: "drawal", label: "Drawal" },
  { id: "injection", label: "Injection" },
];

export function ViewSelector({
  selected,
  onSelectionChange,
  options,
}: {
  selected: string;
  onSelectionChange: (selected: string) => void;
  options: { id: string; label: string }[];
}) {
  return (
    <div className="flex items-center gap-4">
      {options.map((option) => {
        const isSelected = selected === option.id;
        return (
          <button
            key={option.id}
            onClick={() => onSelectionChange(option.id)}
            className="flex items-center gap-2 cursor-pointer focus:outline-none px-2 py-1"
          >
            <div
              className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${
                isSelected ? "border-transparent" : "border-gray-400"
              }`}
              style={{
                backgroundColor: isSelected ? "#34656D" : "transparent",
              }}
            >
              {isSelected && (
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
              )}
            </div>
            <span
              className={`font-medium text-xs transition-colors ${
                isSelected ? "text-gray-800" : "text-gray-500"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function BuySellAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  selectedExchanges = [],
  selectedMarkets = [],
  activeChartTab,
  setSharedData,
  setSharedDeltaData,
  view = "buyers",
  deltaView = "drawal",
  selectedEntities = [],
  deltaSelectedEntities = [],
  selectedFilterDate = [],
  deltaSelectedDates = [],
  onEntityOptionsChange,
  onDeltaEntityOptionsChange,
}: {
  selectedBlock?: any[];
  refreshKey?: number;
  setError?: any;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
  activeChartTab?: string;
  setSharedData?: (data: any) => void;
  setSharedDeltaData?: (data: any) => void;
  view?: "buyers" | "sellers";
  deltaView?: "drawal" | "injection";
  selectedEntities?: { value: string; label: string }[];
  deltaSelectedEntities?: { value: string; label: string }[];
  selectedFilterDate?: any[];
  deltaSelectedDates?: any[];
  onEntityOptionsChange?: (options: { value: string; label: string }[]) => void;
  onDeltaEntityOptionsChange?: (
    options: { value: string; label: string }[],
  ) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TopBuyerSellerResponse | null>(null);

  const activeTab = activeChartTab || DataTabs[0];

  useEffect(() => {
    if (activeTab !== "Buy/Sell") return;

    if (selectedFilterDate.length === 0) {
      setData(null);
      setSharedData?.(null);
      return;
    }

    const { value } = selectedFilterDate[0];
    const [startDate, endDate] = value.split(",");

    const fetchData = async () => {
      try {
        setLoading(true);
        const exch = selectedExchanges;
        const prod = selectedMarkets;

        let response;
        if (selectedBlock && selectedBlock.length > 0) {
          response = await getTopBuyerSellerSlotwise(
            startDate,
            endDate || startDate,
            exch,
            prod,
            selectedBlock,
          );
        } else {
          response = await getTopBuyersSellers(
            startDate,
            endDate || startDate,
            exch,
            prod,
          );
        }

        setData(response);
        setSharedData?.(response);
      } catch (err) {
        console.error("Error fetching top buyers/sellers", err);
        setError?.("Failed to fetch top buyers/sellers data");
        setData(null);
        setSharedData?.(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    selectedFilterDate,
    refreshKey,
    setError,
    activeTab,
    setSharedData,
    selectedExchanges,
    selectedMarkets,
    selectedBlock,
  ]);

  const entityOptions = useMemo(() => {
    if (!data) return [];
    const source = view === "buyers" ? data.top_buyers : data.top_sellers;
    return source.map((item) => ({ value: item.name, label: item.name }));
  }, [data, view]);

  // Notify parent of entity options changes
  useEffect(() => {
    onEntityOptionsChange?.(entityOptions);
  }, [entityOptions, onEntityOptionsChange]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const source = view === "buyers" ? data.top_buyers : data.top_sellers;
    const filtered =
      selectedEntities.length > 0
        ? source.filter((item) =>
            selectedEntities.some((e) => e.value === item.name),
          )
        : source;
    return filtered
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
      .map((item) => ({
        ...item,
        fullName: item.name,
      }));
  }, [data, view, selectedEntities]);

  const legends = useMemo(() => {
    const option = VIEW_OPTIONS.find((o) => o.id === view);
    return [
      {
        dataKey: "value",
        name: option?.label || "Value",
        type: ChartType.Bar,
        unit: "MWh",
      },
    ];
  }, [view]);

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white w-full rounded-2xl p-0 flex flex-col !min-h-[200px]">
      {activeTab === "Buy/Sell" ? (
        loading ? (
          <div className="w-full bg-white flex items-center justify-center h-full flex-1">
            <img src={loader} width={200} alt="loader" className="block" />
          </div>
        ) : !data || chartData.length === 0 ? (
          <div className="w-full bg-white flex items-center justify-center h-full flex-1">
            <p className="text-gray-500">No data available</p>
          </div>
        ) : (
          <ReMixChart
            // tooltipBoundary="viewport"
            chartHeight="calc(100% - 50px)"
            data={chartData}
            legends={legends}
            xDataKey="fullName"
            xAxisTickFormatter={(v) => v.length > 12 ? v.substring(0, 12) + "..." : v}
            isTimeSlot={false}
            yAxisLabel="MWh"
            showBrush={false}
            showToolbox={false}
            top={10}
            bottom={30}
            barCategoryGap={5}
            xAxisInterval={0}
            fontSize={10}
            colorByPoint={true}
            barColors={[
              "#FF6565", // Red
              "#2196f3", // Blue
              "#5BE854", // Green
              "#EE65FF", // Purple
              "#ff9f43", // Orange
              "#EEEE59", // Yellow
              "#ffbe76", // Peach
              "#FF6FBA", // Pink
              "#C9C6C6", // Light Grey
              "#817676", // Dark Grey
            ]}
          />
        )
      ) : (
        <BuySellDelta
          selectedBlock={selectedBlock}
          refreshKey={refreshKey || 0}
          setError={setError}
          selectedExchanges={selectedExchanges}
          selectedMarkets={selectedMarkets}
          view={deltaView}
          setSharedData={setSharedDeltaData}
          comparisonDates={deltaSelectedDates}
          selectedEntities={deltaSelectedEntities}
          onEntityOptionsChange={onDeltaEntityOptionsChange}
        />
      )}
    </div>
  );
}
